import { CheckBoxGroup, Divider, InputContainer, Menu, RadioGroup, RadioLine } from 'components'
import { useEffect } from 'react'
import { Fragment } from 'react/jsx-runtime'
import {
  DraftSession,
  helperLeadProperties,
  HelperLeadProperty,
  SessionTrait,
  TemplateRouting,
} from 'types'
import {
  AdditionalQuestionsProps,
  FormSectionData,
  formTextProps,
  getRouteBasedAdditionalQues,
} from '../../utils'
import FormSection from '../FormSection'

interface Props {
  routing?: TemplateRouting
  previewMode?: boolean
  colorScheme?: string
  templateFormSections: FormSectionData
  userSession: DraftSession
  setUserSession: (updatedSession: DraftSession) => void
  setAdditionalQuestionsAnswered: (answered: boolean) => void
}

export default function AdditionalQuestionsSection({
  routing,
  previewMode,
  colorScheme,
  templateFormSections,
  userSession,
  setUserSession,
  setAdditionalQuestionsAnswered,
}: Props) {
  const additionalFields = getRouteBasedAdditionalQues(
    templateFormSections.additionalFields,
    routing,
    userSession
  )

  useEffect(() => {
    if (!additionalFields) return
    const additionalTitles = additionalFields.map(f => f.prop!)
    const traits = userSession.traits ?? []

    const additionalQuestionsAnswered = additionalFields.every(field => {
      const title = field.prop
      const leadField = field.leadField
      if (!title) return true
      const hasTrait = traits.some(t => t.groupTitle === title)
      if (hasTrait) return true

      if (!leadField) return false
      const value =
        userSession?.lead?.[leadField as HelperLeadProperty] ||
        userSession?.lead?.custom?.[leadField]
      const isBoolean = leadField === 'followUp' || leadField === 'optIn'

      return isBoolean ? true : value !== null && value !== undefined && value !== ''
    })

    setAdditionalQuestionsAnswered(additionalQuestionsAnswered)

    const filteredSelections = traits.filter(s => additionalTitles.includes(s.groupTitle))
    if (filteredSelections.length !== traits.length) {
      setUserSession({
        ...userSession,
        traits: filteredSelections,
      })
    }
  }, [userSession.traits, additionalFields])

  if (!additionalFields) return

  function handleValueSelection(selectedValues: string[], groupTitle: string, leadField?: string) {
    if (leadField) {
      const isCustom = !helperLeadProperties.includes(leadField as HelperLeadProperty)
      const isBoolean = leadField === 'followUp' || leadField === 'optIn'

      if (isCustom && selectedValues.length === 1) {
        setUserSession({
          ...userSession,
          lead: {
            ...userSession.lead,
            custom: {
              ...userSession.lead?.custom,
              [leadField]: selectedValues[0],
            },
          },
        })
      } else if (selectedValues.length === 1) {
        setUserSession({
          ...userSession,
          lead: {
            ...userSession.lead,
            [leadField]: isBoolean ? selectedValues[0] === 'yes' : selectedValues[0],
          },
        })
      }
    } else {
      const field = additionalFields.find(field => field.prop === groupTitle)
      const traits: SessionTrait[] = selectedValues.map(selectedValue => ({
        groupTitle: groupTitle!,
        title: selectedValue,
        traitId: field?.values.find(value => value.label === selectedValue)?.id,
      }))
      const existingSelections = userSession?.traits ?? []
      const updatedTraitSelections = [
        ...existingSelections.filter(s => s.groupTitle !== groupTitle),
        ...traits,
      ]

      setUserSession({
        ...userSession,
        traits: updatedTraitSelections,
      })
    }
  }

  function renderValueInput(field: AdditionalQuestionsProps) {
    const { displayType, values, radioLineParams, prop, isMulti, leadField } = field
    const selectedValues = userSession.traits
      ?.filter(s => s.groupTitle === field.prop)
      .map(selection => selection.title)
    const rawValue =
      userSession?.lead?.[leadField as HelperLeadProperty] ??
      userSession?.lead?.custom?.[leadField!]

    const isBoolean = leadField === 'followUp' || leadField === 'optIn'

    if (isBoolean) {
      const value = rawValue ? 'yes' : rawValue === false ? 'no' : undefined

      return (
        <Menu
          variant="outline"
          borderRadius="sm"
          menuProps={{
            positioning: { sameWidth: true, placement: 'bottom' },
          }}
          portal={!previewMode}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
          value={value}
          onChange={value => handleValueSelection([value], prop, leadField)}
        />
      )
    }

    switch (displayType) {
      case 'select':
      case 'grid':
        return (
          <Menu
            variant="outline"
            borderRadius="sm"
            menuProps={{
              positioning: { sameWidth: true, placement: 'bottom' },
            }}
            isMulti={isMulti}
            portal={!previewMode}
            options={values.map(value => ({
              label: value.label,
              value: value.label,
            }))}
            value={
              leadField
                ? rawValue?.toString()
                : isMulti
                ? selectedValues ?? []
                : selectedValues?.[0]
            }
            onChange={selectedValues =>
              handleValueSelection(
                Array.isArray(selectedValues) ? selectedValues : [selectedValues],
                prop,
                leadField
              )
            }
          />
        )
      case 'list':
        if (isMulti)
          return (
            <CheckBoxGroup
              values={values.map(value => ({
                value: value.label,
                checked:
                  userSession.traits?.some(
                    s => s.groupTitle === field.prop && s.title === value.label
                  ) ?? false,
              }))}
              onChange={selectedValues => handleValueSelection(selectedValues, prop)}
              textProps={formTextProps}
              colorPalette={colorScheme}
            />
          )
        else
          return (
            <RadioGroup
              value={selectedValues?.[0]}
              options={values.map(value => ({ value: value.label }))}
              onChange={selectedValue => handleValueSelection([selectedValue], prop)}
              textProps={formTextProps}
              colorPalette={colorScheme}
            />
          )

      case 'radio-line': {
        const { minValueText, maxValueText, ...rest } = radioLineParams || {}
        const value = selectedValues?.[0] ? Number(selectedValues[0]) : null
        return (
          <RadioLine
            value={value}
            minValueText={minValueText?.en}
            maxValueText={maxValueText?.en}
            onChange={selectedValue =>
              selectedValue && handleValueSelection([selectedValue.toString()], prop)
            }
            textProps={formTextProps}
            colorPalette={colorScheme}
            {...rest}
          />
        )
      }
    }
  }

  return (
    <FormSection key="additional-questions" previewMode={previewMode}>
      {additionalFields.map((field, index) => {
        return (
          <Fragment key={field.title}>
            <InputContainer label={field.title} my={2}>
              {field.values && renderValueInput(field)}
            </InputContainer>
            {index !== additionalFields.length - 1 && <Divider borderColor="rgba(0, 0, 0, 0.1)" />}
          </Fragment>
        )
      })}
    </FormSection>
  )
}
